import React, { useState } from "react";
import {
    ChevronDown,
    ChevronUp,
    Database,
    FolderOpen,
    Globe,
    Loader2,
    Upload,
    Zap,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { INPUT_CLS, SELECT_CLS, SORGU_MODLARI, UPLOAD_ZONES } from "./kusurConstants";
import { KusurSistemEvrakPanel, KusurUploadZone } from "./KusurPanels";

function Accordion({ title, icon: Icon, open, onToggle, badge, children }) {
    return (
        <div className="rounded-lg border border-white/10 bg-[#121A2F]/50 overflow-hidden">
            <button
                type="button"
                onClick={onToggle}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/[0.03]"
            >
                {Icon ? <Icon className="h-3.5 w-3.5 text-[#a31d21] shrink-0" /> : null}
                <span className="text-xs font-medium text-white flex-1">{title}</span>
                {badge != null ? (
                    <span className="text-[10px] font-mono text-slate-500">{badge}</span>
                ) : null}
                {open ? (
                    <ChevronUp className="h-3.5 w-3.5 text-slate-500" />
                ) : (
                    <ChevronDown className="h-3.5 w-3.5 text-slate-500" />
                )}
            </button>
            {open ? <div className="px-3 pb-3 border-t border-white/5 pt-2">{children}</div> : null}
        </div>
    );
}

export default function KusurFormSidebar({
    sorguModu,
    setSorguModu,
    form,
    setField,
    dosyalar,
    filteredDosyalar,
    dosyaArama,
    setDosyaArama,
    dosyaListeLoading,
    dosyaDoldurma,
    jobProgress,
    onDosyaSec,
    loadDosyalar,
    evraklar,
    evrakGruplu,
    selectedEvrakIds,
    toggleEvrak,
    selectOnerilen,
    evrakLoading,
    uploads,
    addFiles,
    removeFile,
    toplamDelil,
    internetArastir,
    setInternetArastir,
    dosyayaUygula,
    setDosyayaUygula,
    temizleTaslak,
}) {
    const [open, setOpen] = useState({ mod: true, dosya: true, form: true, delil: true });

    const toggle = (k) => setOpen((p) => ({ ...p, [k]: !p[k] }));

    return (
        <div className="flex flex-col gap-2 h-full overflow-y-auto pr-1">
            <Accordion
                title="Sorgu Modu"
                icon={sorguModu === "sistem" ? Database : Zap}
                open={open.mod}
                onToggle={() => toggle("mod")}
            >
                <div className="grid grid-cols-2 gap-1.5">
                    {SORGU_MODLARI.map((m) => (
                        <button
                            key={m.id}
                            type="button"
                            onClick={() => {
                                setSorguModu(m.id);
                                if (m.id === "anlik") setField("sigorta_dosyasi_id", "");
                            }}
                            className={`text-left rounded-md border px-2 py-1.5 text-[10px] transition ${
                                sorguModu === m.id
                                    ? "border-[#a31d21]/50 bg-[#a31d21]/15 text-white"
                                    : "border-white/10 text-slate-400 hover:bg-white/5"
                            }`}
                        >
                            <div className="font-medium">{m.label}</div>
                            <div className="text-slate-500 mt-0.5">{m.hint}</div>
                        </button>
                    ))}
                </div>
            </Accordion>

            {sorguModu === "sistem" ? (
                <Accordion
                    title="Sigorta Dosyası"
                    icon={FolderOpen}
                    open={open.dosya}
                    onToggle={() => toggle("dosya")}
                    badge={form.sigorta_dosyasi_id ? `#${form.sigorta_dosyasi_id}` : null}
                >
                    <div className="space-y-2">
                        <Input
                            className={INPUT_CLS}
                            placeholder="Dosya no, plaka, müvekkil…"
                            value={dosyaArama}
                            onChange={(e) => setDosyaArama(e.target.value)}
                        />
                        <select
                            className={SELECT_CLS}
                            value={form.sigorta_dosyasi_id}
                            disabled={dosyaListeLoading || dosyaDoldurma}
                            onChange={(e) => {
                                const v = e.target.value;
                                setField("sigorta_dosyasi_id", v);
                                if (v) onDosyaSec(v);
                            }}
                        >
                            <option value="">
                                {dosyaListeLoading ? "Yükleniyor…" : "— Dosya seç —"}
                            </option>
                            {filteredDosyalar.map((d) => (
                                <option key={d.id} value={d.id}>
                                    {d.label || `${d.dosya_no} · ${d.plaka}`}
                                </option>
                            ))}
                        </select>
                        <div className="flex justify-between text-[10px] text-slate-500">
                            <span>{dosyalar.length} açık dosya</span>
                            <button type="button" className="text-[#a31d21] hover:underline" onClick={() => loadDosyalar(dosyaArama)}>
                                Yenile
                            </button>
                        </div>
                        {dosyaDoldurma ? (
                            <div className="flex items-center gap-1.5 text-[10px] text-sky-300">
                                <Loader2 className="h-3 w-3 animate-spin" />
                                {jobProgress || "Otomatik dolduruluyor…"}
                            </div>
                        ) : null}
                        <KusurSistemEvrakPanel
                            evraklar={evraklar}
                            gruplu={evrakGruplu}
                            selectedIds={selectedEvrakIds}
                            onToggle={toggleEvrak}
                            onSelectAll={selectOnerilen}
                            loading={evrakLoading}
                        />
                    </div>
                </Accordion>
            ) : (
                <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-2 text-[10px] text-amber-100/90">
                    Anlık mod — taslak tarayıcıda saklanır.
                </div>
            )}

            <Accordion title="Olay Bilgileri" open={open.form} onToggle={() => toggle("form")}>
                <div className="space-y-2">
                    <div className="flex justify-end">
                        <button type="button" className="text-[10px] text-slate-500 hover:text-rose-400" onClick={temizleTaslak}>
                            Taslağı temizle
                        </button>
                    </div>
                    <Input className={INPUT_CLS} placeholder="Başlık" value={form.baslik} onChange={(e) => setField("baslik", e.target.value)} />
                    <div className="grid grid-cols-2 gap-1.5">
                        <Input type="date" className={INPUT_CLS} value={form.kaza_tarihi} onChange={(e) => setField("kaza_tarihi", e.target.value)} />
                        <Input className={INPUT_CLS} placeholder="Kaza yeri" value={form.kaza_yeri} onChange={(e) => setField("kaza_yeri", e.target.value)} />
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                        <Input className={INPUT_CLS} placeholder="Plaka A" value={form.plaka_a} onChange={(e) => setField("plaka_a", e.target.value)} />
                        <Input className={INPUT_CLS} placeholder="Plaka B" value={form.plaka_b} onChange={(e) => setField("plaka_b", e.target.value)} />
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                        <Input className={INPUT_CLS} placeholder="Taraf A adı" value={form.taraf_a_adi} onChange={(e) => setField("taraf_a_adi", e.target.value)} />
                        <Input className={INPUT_CLS} placeholder="Taraf B adı" value={form.taraf_b_adi} onChange={(e) => setField("taraf_b_adi", e.target.value)} />
                    </div>
                    <Input
                        className={INPUT_CLS}
                        placeholder="Mevcut kusur % (dosyada)"
                        value={form.mevcut_kusur_orani}
                        onChange={(e) => setField("mevcut_kusur_orani", e.target.value)}
                    />
                    <textarea
                        className="w-full min-h-[56px] rounded-md bg-[#0B1220] border border-white/10 p-2 text-xs text-white"
                        placeholder="Olay özeti"
                        value={form.olay_ozeti}
                        onChange={(e) => setField("olay_ozeti", e.target.value)}
                    />
                    <textarea
                        className="w-full min-h-[56px] rounded-md bg-[#0B1220] border border-white/10 p-2 text-xs text-white"
                        placeholder="Beyanlar"
                        value={form.beyan_metni}
                        onChange={(e) => setField("beyan_metni", e.target.value)}
                    />
                    <label className="flex items-center gap-2 text-[10px] text-slate-400">
                        <input type="checkbox" checked={internetArastir} onChange={(e) => setInternetArastir(e.target.checked)} />
                        <Globe className="h-3 w-3" /> İnternet araştırması
                    </label>
                    {sorguModu === "sistem" ? (
                        <label className="flex items-center gap-2 text-[10px] text-slate-400">
                            <input type="checkbox" checked={dosyayaUygula} onChange={(e) => setDosyayaUygula(e.target.checked)} />
                            Kusuru dosyaya yaz
                        </label>
                    ) : null}
                </div>
            </Accordion>

            <Accordion
                title="Belge Yükleme"
                icon={Upload}
                open={open.delil}
                onToggle={() => toggle("delil")}
                badge={`${toplamDelil} delil`}
            >
                <div className="space-y-1.5">
                    {UPLOAD_ZONES.map((z) => (
                        <KusurUploadZone
                            key={z.key}
                            zone={z}
                            files={uploads[z.key]}
                            onAdd={addFiles}
                            onRemove={removeFile}
                            compact
                        />
                    ))}
                </div>
            </Accordion>
        </div>
    );
}
